import { SupabaseService } from '../services/supabaseService';

/**
 * Exports all user data from Supabase as a JSON file.
 * Includes trades, preferences, portfolio data, trade settings, tax data, misc data, and dashboard config.
 * Supports GZIP compression if 'compressed' is true.
 * @param compressed Whether to use GZIP compression
 * @param onProgress Optional callback for progress updates (0-100)
 */
export const exportAllDataAsJSON = async (compressed: boolean = false, onProgress?: (percent: number) => void) => {
    try {
        console.log(`🚀 Starting full data export (compressed: ${compressed})...`);
        onProgress?.(5);

        const [
            trades,
            preferences,
            portfolioData,
            tradeSettings,
            taxData,
            miscData,
            dashboardConfig,
            chartBlobsMetadata
        ] = await Promise.all([
            SupabaseService.getAllTrades(),
            SupabaseService.getUserPreferences(),
            SupabaseService.getPortfolioData(),
            SupabaseService.getTradeSettings(),
            SupabaseService.getAllTaxData(),
            SupabaseService.getAllMiscData(),
            SupabaseService.getDashboardConfig(),
            SupabaseService.getAllChartImageBlobs()
        ]);

        onProgress?.(20);

        // Fetch full blob data for each metadata item
        console.log(`📸 Fetching ${chartBlobsMetadata.length} chart image blobs...`);
        let completed = 0;
        const total = chartBlobsMetadata.length;

        if (total === 0) {
            onProgress?.(100);
        }

        const chartBlobs = await Promise.all(
            chartBlobsMetadata.map(async (metadata) => {
                try {
                    const blob = await SupabaseService.getChartImageBlob(metadata.id);
                    completed++;
                    if (total > 0) {
                        // Spread the remaining 80% progress
                        const progress = 20 + Math.round((completed / total) * 80);
                        onProgress?.(progress);
                    }
                    return blob;
                } catch (err) {
                    console.error(`Failed to fetch blob ${metadata.id}:`, err);
                    completed++;
                    if (total > 0) {
                        const progress = 20 + Math.round((completed / total) * 80);
                        onProgress?.(progress);
                    }
                    return null;
                }
            })
        );

        const exportData = {
            exportDate: new Date().toISOString(),
            app: 'The Nexus Journal',
            version: '1.0',
            data: {
                trades,
                user_preferences: preferences,
                portfolio_data: portfolioData,
                trade_settings: tradeSettings,
                tax_data: taxData,
                misc_data: miscData,
                dashboard_config: dashboardConfig,
                chart_blobs: chartBlobs.filter(blob => blob !== null)
            }
        };

        const jsonString = JSON.stringify(exportData, null, compressed ? 0 : 2);
        let blob: Blob;
        let filename: string;
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];

        if (compressed) {
            // Use native CompressionStream API for GZIP
            const stream = new Blob([jsonString]).stream();
            const compressedStream = stream.pipeThrough(new (window as any).CompressionStream('gzip'));
            blob = await new Response(compressedStream).blob();
            filename = `nexus_journal_backup_${timestamp}.json.gz`;
        } else {
            blob = new Blob([jsonString], { type: 'application/json' });
            filename = `nexus_journal_backup_${timestamp}.json`;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        console.log(`✅ Data export completed successfully: ${filename}`);
        return true;
    } catch (error) {
        console.error('❌ Failed to export data:', error);
        return false;
    }
};
